import { SERVICE_OVERLOADED_ERROR } from '@common/constants/errors'
import { IResponse } from '@common/interfaces/request'
import { Action } from 'redux'
import { takeEvery, put, select } from 'redux-saga/effects'
import { shouldRedirectToLoginError } from '../../helpers/loginErrorMapping'
import { SocketStatus } from '../connection/actions'
import { socketStatus } from '../connection/selectors'
import { PROFILE_FAILURE } from '../profile/actions'
import { IRootState } from '../reducer'
import { putNotification } from './actions'

export const NETWORK_ERROR_MESSAGE = "Couldn't reach Atlas. Check your connection and try again."

/** superagent's marker for a request that never got an HTTP response (offline, aborted, CORS). */
const isTransportFailure = (error: unknown): boolean =>
  (error as { crossDomain?: boolean } | undefined)?.crossDomain === true

/** Offline, backgrounded or socket down: the connection bar already says so, a toast is noise. */
export const isConnectionKnownDown = (state: IRootState): boolean =>
  !navigator.onLine ||
  document.visibilityState === 'hidden' ||
  socketStatus(state) !== SocketStatus.Connected

export function* notifyAction(action: IResponse & Action) {
  if (
    (
      window as Window & {
        __REDIRECTING_TO_ERROR?: boolean
        __REDIRECTING_TO_LOGIN_ERROR?: boolean
      }
    ).__REDIRECTING_TO_ERROR
  ) {
    return
  }

  if (
    (
      window as Window & {
        __REDIRECTING_TO_ERROR?: boolean
        __REDIRECTING_TO_LOGIN_ERROR?: boolean
      }
    ).__REDIRECTING_TO_LOGIN_ERROR &&
    shouldRedirectToLoginError(action.type, action.error)
  ) {
    return
  }

  const data = action.data as
    | {
        success?: { message: string; code: string }
        info?: { message: string; code: string }
        warning?: { message: string; code: string }
        error?: { message: string; code: string; messagesList?: string[] }
      }
    | undefined

  if (data && data.success) {
    const { message, code } = data.success
    yield put(putNotification({ code, message, type: 'success' }))
  }

  if (data && data.info) {
    const { message, code } = data.info
    yield put(putNotification({ code, message, type: 'info' }))
  }

  // A request that completed but whose outcome needs attention (e.g. a QBO sync
  // that posted some collections and failed others) — amber, not a green check.
  if (data && data.warning) {
    const { message, code } = data.warning
    yield put(putNotification({ code, message, type: 'warning' }))
  }

  if (data && data.error) {
    const { message, code, messagesList } = data.error
    const noShowMessage = messagesList ? true : false

    yield put(
      putNotification({
        code,
        message,
        type: 'error',
        messagesList,
        noShowMessage,
      }),
    )
  }

  if (action.error && action.type !== PROFILE_FAILURE && action.error.code !== 'CUSTOM_ERROR') {
    if (isTransportFailure(action.error)) {
      const isDown = (yield select(isConnectionKnownDown)) as boolean
      if (!isDown) {
        yield put(putNotification({ code: '', message: NETWORK_ERROR_MESSAGE, type: 'error' }))
      }
      return
    }
    const { message, code, status, messagesList } = action.error || {}
    const errorData = (action.error?.data ?? undefined) as { isSilent?: boolean } | undefined
    if (status === 503 && code !== SERVICE_OVERLOADED_ERROR.code) {
      yield put(
        putNotification({
          code: '',
          message:
            'Atlas is undergoing scheduled maintenance at this time. Please check back again soon!',
          type: 'error',
        }),
      )
      return
    }
    if (status === 502) {
      yield put(
        putNotification({
          code: '',
          message:
            'The request took too long to process. Please try again or contact support if the issue persists.',
          type: 'error',
        }),
      )
      return
    }
    if (status === 500 && !message) {
      yield put(
        putNotification({
          code: '',
          message:
            'Sorry we encountered an unexpected error, please try again in a couple of minutes.',
          type: 'error',
        }),
      )
      return
    }
    if (errorData?.isSilent) {
      return
    }
    const noShowMessage = messagesList ? true : false

    yield put(
      putNotification({
        code,
        message,
        messagesList,
        type: 'error',
        noShowMessage,
      }),
    )
  }
}

export function* notify() {
  yield takeEvery('*', notifyAction)
}
